import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { MessageApi } from '../apis/MessageApi';
import { getMessageColor } from '../common/message';
import { useWindowFocus } from '../hooks/useWindowFocus';
import { MasterContext } from './MasterContextProvider';

type MessageInfoType = {
  message: string;
  message_en: string;
  url: string;
  isFirst: boolean | undefined; //初回に受け取ったメッセージか
  popup: boolean; //強制ポップアップ表示フラグ
  color: string;
  bgColor: string;
  iconColor: string;
  failedGetMessage: boolean; //取得処理失敗フラグ
  noOperation: boolean;
};

const initialState: MessageInfoType = {
  message: '',
  message_en: '',
  url: 'https://www.naga-den.com/',
  isFirst: undefined,
  popup: false,
  ...getMessageColor(1),
  failedGetMessage: false,
  noOperation: false
};

// 取得失敗時のメッセージ内容
const errorState: MessageInfoType = {
  message: '情報が取得できません。\nしばらくしてから再度アクセスしてください。',
  message_en: 'Failed to get information.\nPlease try again later.',
  url: 'https://www.naga-den.com/',
  isFirst: undefined,
  popup: false,
  ...getMessageColor(1),
  failedGetMessage: true,
  noOperation: false
};

type MessageInfoContextType = {
  info: MessageInfoType;
  setInfo: React.Dispatch<React.SetStateAction<MessageInfoType>>;
  updateMessage: () => void; //APIリクエストをトリガーする関数
};

export const MessageInfoContext = createContext({} as MessageInfoContextType);

const API_INTERVAL = 60000; //取得タイマーの周期
const WAITING_TIME = 2000; //リクエスト実行の最小間隔

type Props = {
  children: React.ReactNode;
};

// 運行情報を取得～記憶するContext
export const MessageInfoContextProvider: React.FC<Props> = (props) => {
  const { master } = useContext(MasterContext);
  const [info, setInfo] = useState<MessageInfoType>(initialState);
  const [updateTrigger, setUpdateTrigger] = useState(true); //手動更新時にタイマーをリセットするためのstate
  const [trigger, setTrigger] = useState(true); //取得処理を発火させるためのstate

  const focused = useRef(true); //画面がアクティブか
  focused.current = useWindowFocus();
  const firstRef = useRef(true); //初回取得実行フラグ
  const timeRef = useRef(Date.now()); //最終リクエスト時刻

  // 一定間隔で取得処理をトリガー
  useEffect(() => {
    const intervalId = setInterval(() => {
      master.stations.length > 0 && setTrigger((pre) => !pre);
    }, API_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [master.stations.length, updateTrigger]);

  // 更新ボタン押下時はタイマーリセットして、取得処理をトリガー
  const updateMessage = useCallback(() => {
    setUpdateTrigger((pre) => !pre);
    setTrigger((pre) => !pre);
  }, []);

  useEffect(() => {
    const getApi = async () => {
      const res = await MessageApi();
      // console.log('🚀  運行情報：APIデータ', res);
      if (res === null) {
        setInfo(errorState);
        // console.log('🚀  運行情報（エラー）：加工後', errorState);
      } else {
        setInfo((pre) => ({
          message: res.message,
          message_en: res.message_en,
          url: res.url,
          isFirst: pre.isFirst === undefined ? true : false,
          popup: res.popup_flg,
          ...getMessageColor(res.alert_level),
          failedGetMessage: false,
          noOperation: res.not_operation_flg
        }));
        // console.log('🚀  運行情報：加工後', {
        //   message: res.message,
        //   message_en: res.message_en,
        //   url: res.url,
        //   popup: res.popup_flg,
        //   ...getMessageColor(res.alert_level),
        //   failedGetMessage: false,
        //   noOperation: res.not_operation_flg
        // });
      }
    };

    const now = Date.now();
    const diff = now - timeRef.current;
    // 初回または、前回から一定時間以上経過しているときのみリクエスト
    if ((focused.current && diff > WAITING_TIME) || firstRef.current) {
      timeRef.current = now;
      firstRef.current = false;
      getApi();
    }
  }, [trigger]);

  return (
    <MessageInfoContext.Provider value={{ info, setInfo, updateMessage }}>
      {props.children}
    </MessageInfoContext.Provider>
  );
};
